import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthStateService } from '../states/auth.state';

export type AgeVerificationSessionStatus = 'started' | 'pending' | 'approved' | 'failed' | 'expired';

export interface AgeVerificationSession {
  verificationId: string;
  status: AgeVerificationSessionStatus;
  expiresAt: string | null;
  canResume: boolean;
}

export interface AgeVerificationStatus {
  enabled: boolean;
  required: boolean;
  verified: boolean;
  verifiedAt: string | null;
  method: string | null;
  session: AgeVerificationSession | null;
}

export type StartAgeVerificationResult =
  | { status: 'approved'; verificationId: string | null }
  | { status: 'started'; verificationId: string; redirectUrl: string };

export interface ConfirmAgeVerificationResult extends AgeVerificationStatus {
  result: AgeVerificationSessionStatus;
}

/** Error code the API returns (HTTP 403) for users who still need to verify. */
export const AGE_VERIFICATION_REQUIRED = 'AGE_VERIFICATION_REQUIRED';

const RETURN_URL_KEY = 'ageVerificationReturnUrl';

/** True when the logged-in user must complete VerifyMy before using the app. */
export const userNeedsAgeVerification = (user: any): boolean =>
  !!user && user.ageVerificationRequired === true && user.ageVerified !== true;

@Injectable({ providedIn: 'root' })
export class AgeVerificationService {
  private http = inject(HttpClient);
  private authState = inject(AuthStateService);
  private platformId = inject(PLATFORM_ID);
  private baseUrl = `${environment.apiUrl}/age-verification`;

  getStatus(): Observable<AgeVerificationStatus> {
    return this.http.get<any>(`${this.baseUrl}/status`).pipe(
      map(res => res.data as AgeVerificationStatus),
      tap(status => this.syncUser(status)),
    );
  }

  start(): Observable<StartAgeVerificationResult> {
    return this.http.post<any>(`${this.baseUrl}/start`, {}).pipe(
      map(res => res.data as StartAgeVerificationResult),
      tap(result => {
        if (result.status === 'approved') this.markVerified();
      }),
    );
  }

  confirm(verificationId: string): Observable<ConfirmAgeVerificationResult> {
    return this.http.post<any>(`${this.baseUrl}/confirm`, { verificationId }).pipe(
      map(res => res.data as ConfirmAgeVerificationResult),
      tap(status => this.syncUser(status)),
    );
  }

  /** Mirrors the server's answer into the stored user so guards stay in sync. */
  syncUser(status: Pick<AgeVerificationStatus, 'required' | 'verified'>): void {
    this.authState.updateUser({
      ageVerified: status.verified,
      ageVerificationRequired: status.required,
    });
  }

  markVerified(): void {
    this.authState.updateUser({ ageVerified: true, ageVerificationRequired: false });
  }

  markRequired(): void {
    this.authState.updateUser({ ageVerified: false, ageVerificationRequired: true });
  }

  // ── Where to send the user once verified (survives the VerifyMy round trip) ──

  rememberReturnUrl(url: string | null | undefined): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!url || !url.startsWith('/') || url.startsWith('//') || url.startsWith('/age-verification')) return;
    sessionStorage.setItem(RETURN_URL_KEY, url);
  }

  consumeReturnUrl(): string {
    if (!isPlatformBrowser(this.platformId)) return '/all';
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    // Only same-site paths — never an open redirect.
    return url && url.startsWith('/') && !url.startsWith('//') ? url : '/all';
  }
}
